import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SupportTicket } from '@/api/support/supportApi.types';
import type { PlatformUser } from '@/api/users/usersApi.types';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchableSelect';
import { TicketPriorityBadge, TicketStatusBadge } from '@/components/support/TicketBadges';
import { TicketThread } from '@/components/support/TicketThread';
import { TicketTimeline } from '@/components/support/TicketTimeline';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import {
  SUPPORT_PLATFORM_STATUS_TARGETS,
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
} from '@/constants/support';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePlatformUsersQuery } from '@/hooks/usePlatformUsersQuery';
import {
  useAssignSupportTicketMutation,
  useChangeSupportPriorityMutation,
  useChangeSupportStatusMutation,
  useSupportTicketDetailQuery,
  useSupportTicketsQuery,
} from '@/hooks/useSupport';
import { cn } from '@/lib/utils';
import {
  buildPriorityTranslationKey,
  buildStatusTranslationKey,
} from '@/components/support/supportLabels';

export function SupportPage() {
  const { t } = useTranslation('support');
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const selectedTicketId = ticketId === undefined ? null : ticketId;

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [statusFilter, setStatusFilter] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(
    function resetPageOnFilterChange() {
      setPage(1);
    },
    [debouncedSearch, statusFilter, unassignedOnly]
  );

  const ticketsQuery = useSupportTicketsQuery('platform', page, {
    search: debouncedSearch,
    status: statusFilter.length === 0 ? null : statusFilter,
    assigneeUserId: null,
    unassigned: unassignedOnly,
  });
  const ticketsResult = ticketsQuery.data;

  const statusOptions = [
    { value: '', label: t('support:filters.allStatuses') },
    ...SUPPORT_TICKET_STATUSES.map(function toOption(value) {
      return { value, label: t(buildStatusTranslationKey(value)) };
    }),
  ];

  function openTicket(nextTicketId: string) {
    navigate(APPLICATION_ROUTES.adminSupport + '/' + nextTicketId);
  }

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader title={t('support:admin.title')} subtitle={t('support:admin.subtitle')} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className={cn('lg:col-span-1', selectedTicketId === null ? 'block' : 'hidden lg:block')}>
          <div className="space-y-3">
            <ListSearchInput
              value={searchInput}
              onChange={setSearchInput}
              placeholder={t('support:admin.searchPlaceholder')}
            />
            <SearchableSelect
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder={t('support:filters.allStatuses')}
            />
            <label className="flex items-center gap-2 text-[13px] text-zooindigo px-1">
              <input
                type="checkbox"
                checked={unassignedOnly}
                onChange={function toggleUnassigned(changeEvent) {
                  setUnassignedOnly(changeEvent.target.checked);
                }}
              />
              {t('support:filters.unassignedOnly')}
            </label>
            {ticketsQuery.isLoading === true ? (
              <div className="py-10 flex justify-center">
                <LoadingSpinner className="w-6 h-6" />
              </div>
            ) : ticketsQuery.isError === true || ticketsResult === undefined ? (
              <QueryErrorState
                onRetry={function retry() {
                  ticketsQuery.refetch();
                }}
              />
            ) : ticketsResult.items.length === 0 ? (
              <Card padding="lg" shadow={false} className="text-center text-sm text-zoomuted">
                {t('support:admin.empty')}
              </Card>
            ) : (
              <Card padding="none" className="divide-y divide-zooborder">
                {ticketsResult.items.map(function renderRow(ticket) {
                  return (
                    <AdminTicketRow
                      key={ticket.id}
                      ticket={ticket}
                      isSelected={ticket.id === selectedTicketId}
                      onOpen={function open() {
                        openTicket(ticket.id);
                      }}
                    />
                  );
                })}
              </Card>
            )}
            {ticketsResult === undefined ? null : (
              <ListPagination
                page={page}
                pageSize={ADMIN_LIST_PAGE_SIZE}
                totalItems={ticketsResult.totalItems}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>

        <div className={cn('lg:col-span-2', selectedTicketId === null ? 'hidden lg:block' : 'block')}>
          {selectedTicketId === null ? (
            <Card padding="lg" variant="dashed" className="h-full flex items-center justify-center">
              <p className="text-sm text-zoomuted">{t('support:admin.selectPrompt')}</p>
            </Card>
          ) : (
            <AdminTicketDetail
              ticketId={selectedTicketId}
              onBack={function goBack() {
                navigate(APPLICATION_ROUTES.adminSupport);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface AdminTicketRowProps {
  ticket: SupportTicket;
  isSelected: boolean;
  onOpen: () => void;
}

function AdminTicketRow({ ticket, isSelected, onOpen }: AdminTicketRowProps) {
  const { t } = useTranslation('support');
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'w-full px-4 py-3 text-left flex flex-col gap-1.5',
        isSelected === true ? 'bg-zoovpalelavender' : 'hover:bg-zoooffwhite'
      )}
    >
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[13px] font-bold text-zooindigo truncate">{ticket.subject}</span>
        {ticket.unreadCount > 0 ? (
          <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[10px] font-bold shrink-0">
            {ticket.unreadCount}
          </span>
        ) : null}
      </div>
      {ticket.tenantName === null ? null : (
        <div className="text-[11px] text-zoomuted truncate">{ticket.tenantName}</div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        <TicketStatusBadge status={ticket.status} />
        <TicketPriorityBadge priority={ticket.priority} />
        <span className="text-[11px] text-zoomuted">
          {ticket.assigneeName === null ? t('support:admin.unassigned') : ticket.assigneeName}
        </span>
      </div>
    </button>
  );
}

function buildAssigneeOptions(
  unassignedLabel: string,
  loadedPlatformUsers: PlatformUser[],
  currentAssigneeId: string | null,
  currentAssigneeName: string | null
): SearchableSelectOption[] {
  const loadedOptions = loadedPlatformUsers.map(function toOption(platformUser) {
    return { value: platformUser.id, label: platformUser.fullName };
  });
  const currentAssigneeIsListed = loadedOptions.some(function matchesCurrentAssignee(option) {
    return option.value === currentAssigneeId;
  });
  const pinnedAssignee =
    currentAssigneeId === null || currentAssigneeIsListed
      ? []
      : [
          {
            value: currentAssigneeId,
            label: currentAssigneeName === null ? '' : currentAssigneeName,
          },
        ];
  return [{ value: '', label: unassignedLabel }, ...pinnedAssignee, ...loadedOptions];
}

interface AdminTicketDetailProps {
  ticketId: string;
  onBack: () => void;
}

function AdminTicketDetail({ ticketId, onBack }: AdminTicketDetailProps) {
  const { t } = useTranslation('support');
  const detailQuery = useSupportTicketDetailQuery('platform', ticketId);
  const assignMutation = useAssignSupportTicketMutation(ticketId);
  const statusMutation = useChangeSupportStatusMutation(ticketId);
  const priorityMutation = useChangeSupportPriorityMutation(ticketId);
  const [assigneeSearchInput, setAssigneeSearchInput] = useState('');
  const debouncedAssigneeSearch = useDebouncedValue(
    assigneeSearchInput,
    SEARCH_DEBOUNCE_MILLISECONDS
  );
  const platformUsersQuery = usePlatformUsersQuery(1, debouncedAssigneeSearch);
  const detail = detailQuery.data;

  if (detailQuery.isLoading === true || detail === undefined) {
    return (
      <Card padding="lg" className="h-full flex items-center justify-center">
        <LoadingSpinner className="w-6 h-6" />
      </Card>
    );
  }

  const assigneeOptions = buildAssigneeOptions(
    t('support:admin.unassigned'),
    platformUsersQuery.data === undefined ? [] : platformUsersQuery.data.items,
    detail.assigneeUserId,
    detail.assigneeName
  );
  const statusTargetOptions = SUPPORT_PLATFORM_STATUS_TARGETS.map(function toOption(value) {
    return { value, label: t(buildStatusTranslationKey(value)) };
  });
  const priorityOptions = SUPPORT_TICKET_PRIORITIES.map(function toOption(value) {
    return { value, label: t(buildPriorityTranslationKey(value)) };
  });

  const disabledNotice =
    detail.assigneeUserId === null
      ? t('support:admin.assignToReply')
      : t('support:composer.threadClosed');

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
      <Card padding="none" className="xl:col-span-2 flex flex-col h-[70vh] overflow-hidden">
        <div className="px-4 py-3 border-b border-zooborder">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              aria-label={t('support:detail.back')}
              className="lg:hidden text-zoomuted hover:text-zooindigo"
            >
              <Icon name="ArrowLeft" />
            </button>
            <h2 className="flex-1 text-sm font-bold text-zooindigo truncate">{detail.subject}</h2>
          </div>
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <TicketStatusBadge status={detail.status} />
            <TicketPriorityBadge priority={detail.priority} />
            {detail.tenantName === null ? null : (
              <span className="text-[11px] text-zoomuted">{detail.tenantName}</span>
            )}
          </div>
        </div>
        <TicketThread
          scope="platform"
          ticketId={ticketId}
          ownSenderKind="platform"
          isSendEnabled={detail.isSendEnabled}
          disabledNotice={disabledNotice}
        />
      </Card>

      <Card as="section" className="xl:col-span-1 space-y-5">
        <div className="space-y-2">
          <Label>{t('support:admin.assignee')}</Label>
          <SearchableSelect
            options={assigneeOptions}
            value={detail.assigneeUserId === null ? '' : detail.assigneeUserId}
            placeholder={t('support:admin.assignPlaceholder')}
            isLoading={platformUsersQuery.isFetching}
            disableBuiltInFilter
            onInputChange={setAssigneeSearchInput}
            onChange={function changeAssignee(nextAssigneeId) {
              if (nextAssigneeId.length > 0 && nextAssigneeId !== detail.assigneeUserId) {
                assignMutation.mutate(nextAssigneeId);
              }
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('support:admin.changeStatus')}</Label>
          <SearchableSelect
            options={statusTargetOptions}
            value=""
            placeholder={t('support:admin.changeStatusPlaceholder')}
            onChange={function changeStatus(nextStatus) {
              if (nextStatus.length > 0) {
                statusMutation.mutate({ status: nextStatus, note: null });
              }
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('support:admin.changePriority')}</Label>
          <SearchableSelect
            options={priorityOptions}
            value={detail.priority}
            onChange={function changePriority(nextPriority) {
              if (nextPriority !== detail.priority) {
                priorityMutation.mutate(nextPriority);
              }
            }}
          />
        </div>
        <div className="space-y-3 pt-2 border-t border-zooborder">
          <h3 className="text-sm font-bold text-zooindigo">{t('support:detail.timelineTitle')}</h3>
          <TicketTimeline events={detail.events} />
        </div>
      </Card>
    </div>
  );
}
